import { FilterCondition } from '@helo/cms'

import { FilterCriteria } from '../../services/queryService'

export const defaultFilterCriteria: FilterCriteria = {
  columnId: '',
  filterValue: '',
  filterToValue: '',
  filterValueType: FilterCondition.equals as FilterCriteria['filterValueType'],
  condition: FilterCondition.equals,
  operator: FilterCondition.equals as FilterCriteria['operator'],
}

/** GraphQL filter input type for Prisma / subgraph date filters. */
export const DATE_TIME_FILTER_INPUT = 'DateTimeFilterInput'

/** GraphQL filter input type for analytics cube date filters. */
export const CUBE_DATE_TIME_FILTER = 'CubeDateTimeFilter'

/**
 * Date filter dialects the editor understands. Datasource-specific type names
 * map to a dialect here so consumers dispatch on the dialect, not the type
 * name — adding a datasource means extending this module only.
 */
export type DateFilterDialect = 'prisma' | 'analytics'

const DATE_FILTER_DIALECTS: Record<string, DateFilterDialect> = {
  [DATE_TIME_FILTER_INPUT]: 'prisma',
  [CUBE_DATE_TIME_FILTER]: 'analytics',
}

/** Resolves a GraphQL filter input type name to its date filter dialect, if any. */
export const getDateFilterDialect = (
  typeName: string | undefined | null,
): DateFilterDialect | undefined =>
  typeName ? DATE_FILTER_DIALECTS[typeName] : undefined

/** True when the type name is an analytics (cube-style) date filter input. */
export const isAnalyticsDateFilterTypeName = (
  typeName: string | undefined | null,
): boolean => getDateFilterDialect(typeName) === 'analytics'

/** True when the type name is any known date filter input. */
export const isDateFilterTypeName = (
  typeName: string | undefined | null,
): boolean => getDateFilterDialect(typeName) !== undefined

const formatAnalyticsDateValue = (val: string | undefined) => val?.split('T')[0]

const filterCriteriaToAnalyticsDateInput = (
  criteria: FilterCriteria,
): Record<string, unknown> => {
  const { condition, filterValue, filterToValue } = criteria

  switch (condition) {
    case FilterCondition.equals:
      return { equals: [formatAnalyticsDateValue(filterValue as string)] }
    case FilterCondition.notEqual:
      return { notEquals: [formatAnalyticsDateValue(filterValue as string)] }
    case FilterCondition.lessThan:
      return { beforeDate: formatAnalyticsDateValue(filterValue as string) }
    case FilterCondition.greaterThan:
      return { afterDate: formatAnalyticsDateValue(filterValue as string) }
    case FilterCondition.lessThanOrEqual:
      return { beforeOrOnDate: formatAnalyticsDateValue(filterValue as string) }
    case FilterCondition.greaterThanOrEqual:
      return { afterOrOnDate: formatAnalyticsDateValue(filterValue as string) }
    case FilterCondition.inRange:
      return {
        inDateRange: [
          formatAnalyticsDateValue(filterValue as string),
          formatAnalyticsDateValue(filterToValue as string),
        ],
      }
    case FilterCondition.blank:
      return { set: false }
    case FilterCondition.notBlank:
      return { set: true }
    default:
      return {}
  }
}

const filterCriteriaToPrismaDateInput = (
  criteria: FilterCriteria,
): Record<string, unknown> => {
  const { condition, filterValue, filterToValue } = criteria

  switch (condition) {
    case FilterCondition.equals:
      return { equals: filterValue as string }
    case FilterCondition.notEqual:
      return { not: { equals: filterValue as string } }
    case FilterCondition.lessThan:
      return { lt: filterValue as string }
    case FilterCondition.greaterThan:
      return { gt: filterValue as string }
    case FilterCondition.lessThanOrEqual:
      return { lte: filterValue as string }
    case FilterCondition.greaterThanOrEqual:
      return { gte: filterValue as string }
    case FilterCondition.inRange:
      return {
        gte: filterValue as string,
        lte: filterToValue as string,
      }
    case FilterCondition.blank:
      return { equals: null }
    case FilterCondition.notBlank:
      return { not: { equals: null } }
    default:
      return {}
  }
}

const inputToAnalyticsDateFilterCriteria = (
  input: Record<string, unknown>,
): FilterCriteria => {
  if (input.equals?.[0] !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.equals[0] as string,
      condition: FilterCondition.equals,
    }
  }
  if (input.notEquals?.[0] !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.notEquals[0] as string,
      condition: FilterCondition.notEqual,
    }
  }
  if (input.beforeDate !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.beforeDate as string,
      condition: FilterCondition.lessThan,
    }
  }
  if (input.afterDate !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.afterDate as string,
      condition: FilterCondition.greaterThan,
    }
  }
  const inDateRange = input.inDateRange as unknown
  if (
    Array.isArray(inDateRange) &&
    inDateRange[0] !== undefined &&
    inDateRange[1] !== undefined
  ) {
    return {
      ...defaultFilterCriteria,
      filterValue: inDateRange[0] as string,
      filterToValue: inDateRange[1] as string,
      condition: FilterCondition.inRange,
    }
  }
  if (input.set === false) {
    return { ...defaultFilterCriteria, condition: FilterCondition.blank }
  }
  if (input.set === true) {
    return { ...defaultFilterCriteria, condition: FilterCondition.notBlank }
  }
  return defaultFilterCriteria
}

const inputToPrismaDateFilterCriteria = (
  input: Record<string, unknown>,
): FilterCriteria => {
  if (input.equals !== undefined && input.equals !== null) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.equals as string,
      condition: FilterCondition.equals,
    }
  }
  if (input.equals === null) {
    return { ...defaultFilterCriteria, condition: FilterCondition.blank }
  }
  const notEquals = (input.not as Record<string, unknown> | undefined)?.equals
  if (notEquals !== undefined && notEquals !== null) {
    return {
      ...defaultFilterCriteria,
      filterValue: notEquals as string,
      condition: FilterCondition.notEqual,
    }
  }
  if (notEquals === null) {
    return { ...defaultFilterCriteria, condition: FilterCondition.notBlank }
  }
  if (input.lt !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.lt as string,
      condition: FilterCondition.lessThan,
    }
  }
  if (input.gt !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.gt as string,
      condition: FilterCondition.greaterThan,
    }
  }
  if (input.gte !== undefined && input.lte !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.gte as string,
      filterToValue: input.lte as string,
      condition: FilterCondition.inRange,
    }
  }
  if (input.lte !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.lte as string,
      condition: FilterCondition.lessThanOrEqual,
    }
  }
  if (input.gte !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.gte as string,
      condition: FilterCondition.greaterThanOrEqual,
    }
  }
  return defaultFilterCriteria
}

/** Converts UI filter criteria to the wire shape for the given date filter input type. */
export const filterCriteriaToDateFilterInput = (
  criteria: FilterCriteria,
  filterTypeName?: string,
): Record<string, unknown> => {
  if (isAnalyticsDateFilterTypeName(filterTypeName)) {
    return filterCriteriaToAnalyticsDateInput(criteria)
  }
  return filterCriteriaToPrismaDateInput(criteria)
}

/** Parses stored filter input back into UI filter criteria. */
export const inputToDateFilterCriteria = (
  input: Record<string, unknown> | undefined | null,
  filterTypeName?: string,
): FilterCriteria => {
  if (!input || Object.keys(input).length === 0) {
    return defaultFilterCriteria
  }

  if (isAnalyticsDateFilterTypeName(filterTypeName)) {
    return inputToAnalyticsDateFilterCriteria(input)
  }
  return inputToPrismaDateFilterCriteria(input)
}

export const isInRangeDateFilterValue = (
  value: Record<string, unknown> | undefined,
  filterTypeName?: string,
): boolean => {
  if (!value) return false
  if (isAnalyticsDateFilterTypeName(filterTypeName)) {
    const inDateRange = value.inDateRange as unknown
    return (
      Array.isArray(inDateRange) &&
      inDateRange[0] != null &&
      inDateRange[1] != null
    )
  }
  return value.gte != null && value.lte != null
}

export const readDateRangeStartIso = (
  value: Record<string, unknown> | undefined,
  filterTypeName?: string,
): string | undefined => {
  if (!value) return undefined
  if (isAnalyticsDateFilterTypeName(filterTypeName)) {
    const inDateRange = value.inDateRange as unknown
    if (Array.isArray(inDateRange) && typeof inDateRange[0] === 'string') {
      return inDateRange[0]
    }
    return undefined
  }
  if (typeof value.gte === 'string') return value.gte
  return undefined
}

export const readDateRangeEndIso = (
  value: Record<string, unknown> | undefined,
  filterTypeName?: string,
): string | undefined => {
  if (!value) return undefined
  if (isAnalyticsDateFilterTypeName(filterTypeName)) {
    const inDateRange = value.inDateRange as unknown
    if (Array.isArray(inDateRange) && typeof inDateRange[1] === 'string') {
      return inDateRange[1]
    }
    return undefined
  }
  if (typeof value.lte === 'string') return value.lte
  return undefined
}
